import { reactive } from 'vue'
import { api } from '@/plugins/axios'
import event from '@/plugins/events'

// Module-level (not per-component) cache of GET /products/settings results, shared
// across the whole app for the session -- mirrors cash-server's own hourly
// getResolvedProductSettings cache, just fetched lazily per (product_category, sku)
// pair instead of bulk-refreshed, since that's the shape the endpoint exposes.
const cache = reactive({})
const pending = {}

const cacheKey = (productCategory, sku) => `${productCategory}::${sku || ''}`

export function useProductSettings() {
  const fetchProductSettings = (productCategory, sku) => {
    if (!productCategory) return Promise.resolve(null)
    const key = cacheKey(productCategory, sku)
    if (cache[key]) return Promise.resolve(cache[key])
    if (!pending[key]) {
      const params = sku ? { product_category: productCategory, sku } : { product_category: productCategory }
      pending[key] = api.get('/products/settings', { params })
        .then(res => { cache[key] = res; return res })
        .catch(e => { event.error(e); return null })
        .finally(() => { delete pending[key] })
    }
    return pending[key]
  }

  // Synchronous read for use directly in computed/template code. Returns `fallback`
  // until the background fetch resolves and populates the reactive cache -- callers
  // pass the pre-cutover hardcoded behavior as `fallback` so there's no regression
  // during the brief window before the first fetch for a given key completes.
  const getProductSettings = (productCategory, sku, fallback = {}) => {
    if (!productCategory) return fallback
    const key = cacheKey(productCategory, sku)
    if (!cache[key]) {
      fetchProductSettings(productCategory, sku)
      return fallback
    }
    return cache[key]
  }

  return { fetchProductSettings, getProductSettings }
}
